/**
 * High-performance cookie parsing utilities
 * Optimized for server-side cookie extraction with minimal overhead
 */

/**
 * Extract a single cookie value efficiently using regex
 * Best for when you need only one specific cookie
 */
export function getCookieValue(
  cookieString: string,
  cookieName: string
): string | null {
  if (!cookieString) return null

  // Use regex to find the specific cookie - very performatic for single lookups
  const regex = new RegExp(`(?:^|;\\s*)${cookieName}=([^;]*)`)
  const match = cookieString.match(regex)

  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1])
    } catch {
      return match[1] // Return raw value if decoding fails
    }
  }

  return null
}

/**
 * Extract multiple cookie values in a single iteration
 * Much more performatic than multiple regex calls when you need several cookies
 */
export function getCookieValues(
  cookieString: string,
  cookieNames: string[]
): Record<string, string | null> {
  const result: Record<string, string | null> = {}

  // Initialize all requested cookies as null
  cookieNames.forEach((name) => {
    result[name] = null
  })

  if (!cookieString) return result

  // Create a Set for O(1) lookup performance
  const targetCookies = new Set(cookieNames)
  let foundCount = 0

  // Single iteration through cookies - much more efficient than multiple regex
  const cookies = cookieString.split(';')

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')

    if (name && value && targetCookies.has(name)) {
      try {
        result[name] = decodeURIComponent(value)
      } catch {
        result[name] = value // Use raw value if decoding fails
      }

      foundCount++

      // Early exit optimization - stop when we found all requested cookies
      if (foundCount === cookieNames.length) {
        break
      }
    }
  }

  return result
}

/**
 * Type-safe wrapper for tracking data cookies specifically
 * Optimized for the most common use case in the application
 */
export interface TrackingCookies {
  trackingConsented: boolean | null
  sessionId: string | null
  ajsUserId: string | null
  ajsAnonymousId: string | null
}

export function getTrackingCookies(cookieString: string): TrackingCookies {
  const cookies = getCookieValues(cookieString, [
    'trackingConsented',
    'connect.sid',
    'ajs_user_id',
    'ajs_anonymous_id'
  ])

  return {
    trackingConsented: cookies['trackingConsented']
      ? cookies['trackingConsented'] === 'true'
      : null,
    sessionId: cookies['connect.sid'],
    ajsUserId: cookies['ajs_user_id'],
    ajsAnonymousId: cookies['ajs_anonymous_id']
  }
}
